import {
  forwardRef,
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentProps,
  useEffect,
  useMemo,
  useState,
} from "react";

import { InputMask } from "@react-input/mask";
import { useTranslation } from "react-i18next";
import Loading from "react-loading";
import { useDispatch } from "react-redux";

import { CircularButton } from "components";

import { SetUserCreateDistrict } from "store/modules/userCreateUnit/actions";

import { ContainerLoading } from "pages/app/ServiceIntegrationNotification/styles";

import { BaseBadge } from "components/BaseBadge";
import { CardAdminItem } from "components/CardAdminItem";
import GeneralInput from "components/GeneralInput";
import Switch from "components/Switch";

import { isProduct, ProductId } from "multitenancy/product";

import { AttendantRoleService } from "services/attendantRole";

import { buildMaskConfig } from "utils/mask";

import {
  ContainerFormOneLabel,
  ContainerFormOneLabelSwitch,
  ContainerTitleWithNumber,
  ListUnitArea,
  Title,
  UnitArea,
} from "./styles";

type MaskedGeneralInputProps = Omit<
  ComponentProps<typeof GeneralInput>,
  "onChange"
> & {
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

const MaskedGeneralInput = forwardRef<
  HTMLInputElement,
  MaskedGeneralInputProps
>(({ onChange, ...inputProps }, ref) => (
  <GeneralInput
    {...inputProps}
    inputRef={ref}
    onChange={(value) =>
      onChange?.({
        target: { value },
      } as ChangeEvent<HTMLInputElement>)
    }
  />
));

MaskedGeneralInput.displayName = "MaskedGeneralInput";

const CPF_MASK = "999.999.999-99";

export interface IProps {
  userId: number | undefined;
  name: string;
  email: string;
  onChangeEmail: (value: string) => void;
  onChangeName: (value: string) => void;
  cpf: string;
  onChangeCpf: (value: string) => void;
  isActive: boolean;
  onChangeSwitch: (e: boolean) => unknown;
  userCreateDistrictList: any;
  openModalVincule: () => unknown;
  errorName: boolean;
  errorEmail: boolean;
  errorCpf: boolean;
  onChangeExtraContent: (e: any) => unknown;
  extraContent: any;
  isEdit: boolean;
  onClick: (item: any) => void;
  items?: any;
  onClickDelete?: (props: any) => void;
  isEditCreated: boolean;
  isMasterProfile: boolean;
}

export const CreateUserComponent = ({
  userId,
  name,
  email,
  cpf,
  onClick,
  onChangeEmail,
  onChangeCpf,
  extraContent,
  onChangeName,
  onChangeSwitch,
  isActive,
  userCreateDistrictList,
  openModalVincule,
  errorName,
  errorEmail,
  errorCpf,
  isEdit,
  isEditCreated,
  isMasterProfile,
}: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const isCpfRequired = isProduct(ProductId.Audiencias);
  const cpfMaskConfig = useMemo(() => buildMaskConfig(CPF_MASK), []);

  useEffect(() => {
    if (extraContent) {
      onChangeEmail(extraContent.email);
      onChangeName(extraContent.name);
      onChangeCpf(extraContent.cpf ?? extraContent.document ?? "");
    }

    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  async function getDataUnit() {
    if (userId) {
      const { data } = await AttendantRoleService.getAllByUser(userId);
      dispatch(
        SetUserCreateDistrict({
          userData: data?.data,
          errorName: !name,
          errorEmail: !email,
        }),
      );
    }
  }

  useEffect(() => {
    getDataUnit();
  }, [userId]);

  function compared(a: any, b: any) {
    if (a.trigger < b.trigger) return -1;
    if (a.trigger > b.trigger) return 1;
    return 0;
  }

  console.log("isMasterProfile", isMasterProfile);

  return (
    <ContainerFormOneLabel>
      <ContainerFormOneLabelSwitch>
        <Switch
          dataTest="switch_active"
          checked={isActive}
          onChange={() => onChangeSwitch(!isActive)}
          label={t("common.active")}
        />
      </ContainerFormOneLabelSwitch>
      <GeneralInput
        id="name"
        label={t("common.name")}
        name="formOneLabel"
        onChange={onChangeName}
        value={name}
        error={errorName}
        required
        marginRight="0px"
        maxLength={90}
      />
      <div style={{ marginTop: "16px" }}>
        <GeneralInput
          id="email"
          label={t("common.email")}
          name="formOneLabel"
          onChange={onChangeEmail}
          value={email}
          error={errorEmail}
          required
          disabled={isMasterProfile ? false : isEdit || isEditCreated}
          marginRight="0px"
          maxLength={90}
        />
      </div>
      <div style={{ marginTop: "16px" }}>
        <InputMask
          component={MaskedGeneralInput}
          mask={cpfMaskConfig.mask}
          replacement={cpfMaskConfig.replacement}
          id="cpf"
          label="CPF"
          name="cpf"
          onChange={(event) => onChangeCpf(event.target.value)}
          value={cpf}
          error={errorCpf}
          required={isCpfRequired}
          marginRight="0px"
          maxLength={14}
        />
      </div>

      <UnitArea>
        <ContainerTitleWithNumber>
          <Title style={{ marginRight: 10 }}>
            {t("units.linkedUnits")}
            <BaseBadge
              color="primary"
              content={
                userCreateDistrictList?.userData?.length > 0
                  ? userCreateDistrictList?.userData?.filter(
                      (e: any) => e.active || e.itemType,
                    ).length
                  : 0
              }
            />
          </Title>
          <CircularButton id="add_icon" icon="add" onClick={openModalVincule} />
        </ContainerTitleWithNumber>
      </UnitArea>
      {loading ? (
        <ContainerLoading>
          <Loading width={40} type="spin" color="#000" />
        </ContainerLoading>
      ) : (
        <ListUnitArea>
          {userCreateDistrictList?.userData
            ?.sort(compared)
            ?.map((item: any) => (
              <CardAdminItem
                key={item.id}
                isActive={isActive}
                id={item?.id}
                name={
                  !item?.id && item.type === 0 ? item.courtLabel : item.place
                }
                description={item?.profile}
                active={item?.active}
                onClickEditAction={() => onClick(item)}
                disable={false}
              />
            ))}
        </ListUnitArea>
      )}
    </ContainerFormOneLabel>
  );
};
